import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { Button, Input, DatePicker, Spin } from "antd";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { toast } from "../../components/toasts/toastStore";
import { PATH } from "../../redux/utils";

import "./EventoDetallePage.css";

const authHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({ baseURL: PATH });

export default function PerfilPage() {
  const [perfil, setPerfil] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [showPwd, setShowPwd] = useState({ current: false, new: false, confirm: false });
  const [changingPwd, setChangingPwd] = useState(false);

  const fetchPerfil = useCallback(() => {
    setLoading(true);
    api.get("/users/me/perfil", { headers: authHeader() })
      .then((res) => setPerfil(res.data))
      .catch(() => toast.error("No se pudo cargar el perfil"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPerfil(); }, [fetchPerfil]);

  const startEditing = () => {
    setForm({
      nombre: perfil.perfil_nombre || "",
      apellido: perfil.apellido || "",
      telefono: perfil.telefono || "",
      fecha_nacimiento: perfil.fecha_nacimiento ? dayjs(perfil.fecha_nacimiento) : null,
    });
    setEditing(true);
  };

  const handleGuardar = async () => {
    if (!form.nombre?.trim()) return toast.warning("El nombre es obligatorio");
    setSaving(true);
    try {
      const { data } = await api.patch(
        "/users/me/perfil",
        {
          nombre: form.nombre.trim(),
          apellido: form.apellido?.trim() || "",
          telefono: form.telefono?.trim() || null,
          fecha_nacimiento: form.fecha_nacimiento ? form.fecha_nacimiento.format("YYYY-MM-DD") : null,
        },
        { headers: authHeader() }
      );
      setPerfil(data);
      setEditing(false);
      toast.success("Perfil actualizado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/users/me/imagen", fd, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
      });
      const avatarUrl = `${PATH}${data.path}`;
      localStorage.setItem("userAvatar", avatarUrl);
      window.dispatchEvent(new CustomEvent("avatar-updated", { detail: avatarUrl }));
      toast.success("Foto actualizada");
      fetchPerfil();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir la foto");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleChangePassword = async () => {
    if (!pwd.current_password || !pwd.new_password || !pwd.confirm) {
      return toast.warning("Completa los 3 campos");
    }
    if (pwd.new_password !== pwd.confirm) {
      return toast.warning("La nueva contrasena y la confirmacion no coinciden");
    }
    if (pwd.new_password.length < 6) {
      return toast.warning("La nueva contrasena debe tener minimo 6 caracteres");
    }
    setChangingPwd(true);
    try {
      await api.post(
        "/users/me/change-password",
        { current_password: pwd.current_password, new_password: pwd.new_password },
        { headers: authHeader() }
      );
      toast.success("Contrasena actualizada");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cambiar la contrasena");
    } finally {
      setChangingPwd(false);
    }
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!perfil) return null;

  const avatarUrl = perfil.path ? `${PATH}${perfil.path}` : null;
  const nombreCompleto = [perfil.perfil_nombre || perfil.nombre, perfil.apellido].filter(Boolean).join(" ");

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ position: "relative" }}>
                <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", fontSize: 24, fontWeight: 700, color: "#f90336" }}>
                  {avatarUrl ? <img src={avatarUrl} alt="avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (perfil.usuario || "?").slice(0, 2).toUpperCase()}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{ position: "absolute", bottom: -2, right: -2, width: 26, height: 26, borderRadius: "50%", background: "#f90336", border: "2px solid #fff", color: "#fff", cursor: "pointer" }}
                  title="Cambiar foto"
                >
                  <i className="fa-solid fa-camera" style={{ fontSize: 11 }} />
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
              </div>
              <div>
                <div className="cd-header-name-row">
                  <h1 className="cd-client-name">{nombreCompleto || perfil.usuario}</h1>
                </div>
                <div className="cd-header-meta">
                  <span className="cd-meta-item"><i className="fa-solid fa-user" /> {perfil.usuario}</span>
                  <span className="cd-meta-item"><i className="fa-solid fa-shield" /> {perfil.rol}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "info" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("info")}>
              <i className="fa-solid fa-id-card" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "seguridad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("seguridad")}>
              <i className="fa-solid fa-lock" /> Seguridad
            </button>
          </div>
        </div>

        {activeTab === "info" && (
          <div className="cd-card">
            <div className="cd-card-header" style={{ justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-id-card"></i></div>
                <h2 className="cd-card-title">Datos personales</h2>
              </div>
              {!editing ? (
                <Button onClick={startEditing}>Editar</Button>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <Button onClick={() => setEditing(false)} disabled={saving}>Cancelar</Button>
                  <Button type="primary" loading={saving} onClick={handleGuardar} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                    Guardar
                  </Button>
                </div>
              )}
            </div>

            {!editing ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                <div><span className="cd-field-label">Nombre</span><span className="cd-field-value">{perfil.perfil_nombre || "—"}</span></div>
                <div><span className="cd-field-label">Apellido</span><span className="cd-field-value">{perfil.apellido || "—"}</span></div>
                <div><span className="cd-field-label">Telefono</span><span className="cd-field-value">{perfil.telefono || "—"}</span></div>
                <div><span className="cd-field-label">Fecha de nacimiento</span><span className="cd-field-value">{perfil.fecha_nacimiento ? dayjs(perfil.fecha_nacimiento).format("DD/MM/YYYY") : "—"}</span></div>
                <div><span className="cd-field-label">Correo de recuperacion</span><span className="cd-field-value" style={{ color: "#9ca3af" }}>{perfil.email_recuperacion || "—"}</span></div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, maxWidth: 560 }}>
                <div>
                  <div className="paq-field-label">Nombre</div>
                  <Input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
                </div>
                <div>
                  <div className="paq-field-label">Apellido</div>
                  <Input value={form.apellido} onChange={(e) => setForm((f) => ({ ...f, apellido: e.target.value }))} />
                </div>
                <div>
                  <div className="paq-field-label">Telefono</div>
                  <Input value={form.telefono} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} />
                </div>
                <div>
                  <div className="paq-field-label">Fecha de nacimiento</div>
                  <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" value={form.fecha_nacimiento} onChange={(v) => setForm((f) => ({ ...f, fecha_nacimiento: v }))} />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "seguridad" && (
          <>
            <div className="cd-card" style={{ marginBottom: 16 }}>
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock-rotate-left"></i></div>
                <h2 className="cd-card-title">Actividad de sesion</h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><span className="cd-field-label">Correo de recuperacion</span><span className="cd-field-value">{perfil.email_recuperacion || "—"}</span></div>
                <div><span className="cd-field-label">Ultimo inicio de sesion</span><span className="cd-field-value">{perfil.ultima_sesion ? dayjs(perfil.ultima_sesion).format("DD/MM/YYYY, HH:mm") : "—"}</span></div>
              </div>
            </div>

            <div className="cd-card">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-key"></i></div>
                <h2 className="cd-card-title">Cambiar contrasena</h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 360 }}>
                <Input.Password
                  placeholder="Contrasena actual"
                  value={pwd.current_password}
                  onChange={(e) => setPwd((f) => ({ ...f, current_password: e.target.value }))}
                  visibilityToggle={{ visible: showPwd.current, onVisibleChange: (v) => setShowPwd((s) => ({ ...s, current: v })) }}
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
                <Input.Password
                  placeholder="Nueva contrasena"
                  value={pwd.new_password}
                  onChange={(e) => setPwd((f) => ({ ...f, new_password: e.target.value }))}
                  visibilityToggle={{ visible: showPwd.new, onVisibleChange: (v) => setShowPwd((s) => ({ ...s, new: v })) }}
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
                <Input.Password
                  placeholder="Confirmar nueva contrasena"
                  value={pwd.confirm}
                  onChange={(e) => setPwd((f) => ({ ...f, confirm: e.target.value }))}
                  visibilityToggle={{ visible: showPwd.confirm, onVisibleChange: (v) => setShowPwd((s) => ({ ...s, confirm: v })) }}
                  iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
                />
                <Button type="primary" loading={changingPwd} onClick={handleChangePassword} style={{ backgroundColor: "#f90336", borderColor: "#f90336", width: 200 }}>
                  Cambiar contrasena
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
